import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Table2 } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { DataSet } from '../types';
import { formatBytes, formatDate, formatNumber } from '../mockData';
import FileAnalyticsAPI from '../fileAnalyticsService';

interface DatasetDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dataset: DataSet | null;
}

interface PreviewState {
  loading: boolean;
  columns: string[];
  rows: any[][];
  total: number;
  error?: string;
}

const PAGE_SIZE = 50;

const DatasetDetailsModal: React.FC<DatasetDetailsModalProps> = ({ isOpen, onClose, dataset }) => {
  const [preview, setPreview] = useState<PreviewState>({ loading: false, columns: [], rows: [], total: 0 });
  const [page, setPage] = useState(1);

  const totalPages = preview.total > 0 ? Math.ceil(preview.total / PAGE_SIZE) : 1;

  const loadPage = useCallback(async (p: number) => {
    if (!dataset || dataset.status !== 'Ready') return;
    setPreview(prev => ({ ...prev, loading: true, error: undefined }));
    try {
      const data = await FileAnalyticsAPI.previewDataset(dataset.id, PAGE_SIZE, (p - 1) * PAGE_SIZE);
      setPreview({
        loading: false,
        columns: data.columns ?? [],
        rows: data.rows ?? [],
        total: data.total ?? dataset.row_count,
      });
    } catch (err: any) {
      setPreview({
        loading: false,
        columns: [],
        rows: [],
        total: 0,
        error: err?.response?.data?.detail || err?.message || 'Preview failed',
      });
    }
  }, [dataset]);

  useEffect(() => {
    if (!isOpen || !dataset) {
      setPreview({ loading: false, columns: [], rows: [], total: 0 });
      setPage(1);
      return;
    }
    if (dataset.status !== 'Ready') {
      setPreview({ loading: false, columns: [], rows: [], total: 0, error: `Dataset is ${dataset.status} — preview available when status = Ready` });
      return;
    }
    setPage(1);
    loadPage(1);
  }, [isOpen, dataset]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToPage = (p: number) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    loadPage(p);
  };

  if (!dataset) return null;

  const fmtCell = (v: any): string => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') return formatNumber(v);
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    return String(v);
  };

  const rowStart = (page - 1) * PAGE_SIZE + 1;
  const rowEnd = rowStart + preview.rows.length - 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>{dataset.file_name}</Modal.Header>
      <Modal.Body>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          <div className="fa-kpi-card" style={{ padding: 10 }}>
            <div className="fa-kpi-label">Rows</div>
            <div className="fa-kpi-value" style={{ fontSize: 18 }}>{formatNumber(dataset.row_count)}</div>
          </div>
          <div className="fa-kpi-card" style={{ padding: 10 }}>
            <div className="fa-kpi-label">Columns</div>
            <div className="fa-kpi-value" style={{ fontSize: 18 }}>{dataset.columns.length}</div>
          </div>
          <div className="fa-kpi-card" style={{ padding: 10 }}>
            <div className="fa-kpi-label">Size</div>
            <div className="fa-kpi-value" style={{ fontSize: 18 }}>{formatBytes(dataset.size_bytes)}</div>
          </div>
          <div className="fa-kpi-card" style={{ padding: 10 }}>
            <div className="fa-kpi-label">Format</div>
            <div className="fa-kpi-value" style={{ fontSize: 18 }}>.{dataset.format}</div>
          </div>
        </div>

        <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          <strong>S3 location:</strong> <code style={{ fontFamily: 'monospace', fontSize: 11 }}>{dataset.s3_key || '—'}</code><br />
          <strong>Uploaded:</strong> {formatDate(dataset.uploaded_at)} by user #{dataset.uploaded_by || '—'}
          &nbsp;·&nbsp; <strong>Version:</strong> {dataset.version}
          &nbsp;·&nbsp; <strong>Kind:</strong> {dataset.kind === 'raw' ? 'Raw' : dataset.kind === 'aggregation' ? 'Aggregation Output' : 'Rule Output'}
        </div>

        {dataset.columns.length > 0 && (
          <>
            <h4 className="form-label">
              Metadata — Columns ({dataset.columns.length})
            </h4>
            <table className="fa-meta-table">
              <thead>
                <tr>
                  <th className="table-list-th table-list-cell--sno">S.No</th>
                  <th>Column name</th>
                  <th>Data type</th>
                  <th>Nulls</th>
                  <th>Distinct</th>
                  <th>Sample values</th>
                </tr>
              </thead>
              <tbody>
                {dataset.columns.map((c, i) => (
                  <tr key={c.name}>
                    <td>{i + 1}</td>
                    <td><code style={{ fontSize: 11 }}>{c.name}</code></td>
                    <td><span className={`fa-meta-type fa-meta-type--${c.data_type}`}>{c.data_type}</span></td>
                    <td>{c.null_count != null ? formatNumber(c.null_count) : '-'}</td>
                    <td>{c.distinct_count != null ? formatNumber(c.distinct_count) : '-'}</td>
                    <td style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                      {c.sample_values?.join(', ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 8 }}>
          <h4 className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
            <Table2 size={14} /> Data Preview
            {preview.rows.length > 0 && (
              <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                (rows {formatNumber(rowStart)}–{formatNumber(rowEnd)} of {formatNumber(preview.total || dataset.row_count)})
              </span>
            )}
          </h4>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || preview.loading}
                style={{ padding: '2px 8px' }}
              >
                <ChevronLeft size={14} />
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || preview.loading}
                style={{ padding: '2px 8px' }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        {preview.loading && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
            Loading rows from S3 via DuckDB…
          </div>
        )}

        {preview.error && (
          <div style={{ padding: 12, background: 'rgba(239, 68, 68, 0.08)', color: 'rgb(185,28,28)', borderRadius: 6, fontSize: 12 }}>
            {preview.error}
          </div>
        )}

        {!preview.loading && !preview.error && preview.rows.length > 0 && (
          <div style={{ overflow: 'auto', maxHeight: 400, border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
            <table className="fa-meta-table" style={{ minWidth: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th className="table-list-th table-list-cell--sno">S.No</th>
                  {preview.columns.map(c => (
                    <th key={c}><code style={{ fontSize: 11 }}>{c}</code></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, ri) => (
                  <tr key={ri}>
                    <td style={{ color: 'var(--text-tertiary)' }}>{rowStart + ri}</td>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }} title={fmtCell(cell)}>
                        {fmtCell(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!preview.loading && !preview.error && preview.rows.length === 0 && dataset.status === 'Ready' && (
          <div className="empty-state" style={{ padding: 20 }}>
            <p className="empty-state-title">No rows in this dataset</p>
            <p className="empty-state-desc">The Parquet file is valid but empty (0 rows).</p>
          </div>
        )}

        {totalPages > 1 && !preview.loading && preview.rows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => goToPage(1)}
              disabled={page <= 1}
            >
              First
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Page {page} / {totalPages} &nbsp;·&nbsp; {formatNumber(PAGE_SIZE)} rows/page
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
            >
              Next <ChevronRight size={14} />
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => goToPage(totalPages)}
              disabled={page >= totalPages}
            >
              Last
            </button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DatasetDetailsModal;
